import './SelectLabelingRulesModal.scss';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdCancel, MdCheck } from 'react-icons/md';
import { Modal, Nav } from 'react-bootstrap';
import cn from 'classnames';
import {
  isBillingSegmentName,
  SegmentationMappingLabelRule
} from '@blisspointmedia/bpm-types/dist/SegmentationMapping';
import { Button } from '@Components/Button';
import { Checkbox } from '@Components/FormCheck';
import { BPMTable, Header } from '@Components/StickyTable';
import { Dropdown } from '@Components/Dropdown';
import { FilterBar } from '@Components/FilterBar';
import { Appearance, Design, Placement, Size } from '@Components/designConstants';
import { Brand60 } from '../../utils/colors';
import { useStateFunction } from '../../utils/hooks/useData';
import {
  BillingMappingLabelRule,
  CustomSegmentsData,
  SystemGeneratedSegmentNames
} from './segmentation-mapping-constants';
import { buildIfStatement, formatDateRange } from './segmentationMappingUtils';

export interface ModifiedSegmentationMappingLabelRule extends SegmentationMappingLabelRule {
  ifStatement?: string;
  segmentName?: string;
  segmentValueName?: string;
}

export interface ModifiedBillingMappingLabelRule extends BillingMappingLabelRule {
  ifStatement?: string;
  segmentName?: string;
  segmentValueName?: string;
}

interface SelectLabelingRulesModalProps {
  customSegments: CustomSegmentsData[] | undefined;
  labelingRules: SegmentationMappingLabelRule[];
  billingRules: BillingMappingLabelRule[];
  onRuleApply: (
    rules: (ModifiedSegmentationMappingLabelRule | ModifiedBillingMappingLabelRule)[]
  ) => void;
  show: boolean;
  setShow: (show: boolean) => void;
}

interface Tab {
  label: string;
  key: string;
}

interface RuleHeader extends Header {
  key: string;
}

export const SelectLabelingRulesModal = ({
  customSegments,
  labelingRules,
  billingRules,
  onRuleApply,
  show,
  setShow
}: SelectLabelingRulesModalProps) => {
  const [selectedRules, setSelectedRules] = useState<
    (SegmentationMappingLabelRule | ModifiedBillingMappingLabelRule)[]
  >([]);
  const [activeTab, setActiveTab] = useState<string>('all');
  // Bumped on tab/filter change to remount the table, resetting its page (and sort) to the top.
  const [tableResetKey, setTableResetKey] = useState<number>(0);
  const [screenWidth, setScreenWidth] = useState<number>(window.innerWidth);
  const [modalWidth, setModalWidth] = useState<number>(0);
  const [isSaving, setIsSaving] = useState(false);

  const limit = 6;

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const modalRef = useRef<HTMLDivElement>(null);

  const allActiveRules = useMemo(
    () => labelingRules.filter(rule => rule.filterTokens.length > 0 && rule.isActive),
    [labelingRules]
  );

  const allActiveBillingRules = useMemo(
    () => billingRules.filter(rule => rule.filterTokens.length > 0 && rule.isActive),
    [billingRules]
  );

  useEffect(() => {
    setSelectedRules([...allActiveRules, ...allActiveBillingRules]);
  }, [allActiveRules, allActiveBillingRules]);

  const ruleTabs = useMemo(() => {
    const tabs: Tab[] = [{ label: 'All Segments', key: 'all' }];
    const customTabs =
      customSegments
        ?.filter(segment => {
          const systemNames = Object.values(SystemGeneratedSegmentNames).map(name => String(name));
          return !systemNames.includes(segment.segmentName);
        })
        .map(segment => ({
          label: segment.segmentName,
          key: segment.segmentName
        })) || [];
    return tabs.concat(customTabs);
  }, [customSegments]);

  useEffect(() => {
    if (!show) return undefined;
    const timer = setTimeout(() => {
      const modal = document.querySelector('.select-labeling-rules-modal .modal-dialog');
      if (modal) {
        const { width } = modal.getBoundingClientRect();
        setModalWidth(width);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [show, screenWidth]);

  const visibleTabs = useMemo(() => {
    const avgCharLength = 11;
    const padding = 20;

    let tabWidthSum = 0;
    let maxVisibleTabs = 0;
    const maxOverflowLabelLength =
      avgCharLength * Math.max(...ruleTabs.map(item => item.label.length));

    while (maxVisibleTabs < ruleTabs.length && tabWidthSum < modalWidth - maxOverflowLabelLength) {
      tabWidthSum += padding + avgCharLength * ruleTabs[maxVisibleTabs].label.length;
      maxVisibleTabs++;
    }
    if (maxVisibleTabs === ruleTabs.length) {
      maxVisibleTabs += 1;
    }
    return ruleTabs.slice(0, maxVisibleTabs);
  }, [ruleTabs, modalWidth]);

  const overflowTabs = useMemo(() => ruleTabs.slice(visibleTabs.length), [ruleTabs, visibleTabs]);

  const labelingRuleIfStatement = (
    rule: SegmentationMappingLabelRule | BillingMappingLabelRule
  ) => {
    if (rule.isAdvanced == null) {
      return '';
    }
    return buildIfStatement(rule.filterTokens, rule.isAdvanced);
  };

  const availableRules = useMemo<
    (ModifiedSegmentationMappingLabelRule | ModifiedBillingMappingLabelRule)[]
  >(() => {
    const activeRules = [...labelingRules, ...billingRules]
      .filter(rule => rule.filterTokens.length > 0 && rule.isActive)
      .map(rule => ({
        ...rule,
        ifStatement: labelingRuleIfStatement(rule),
        segmentName:
          'setBillingSegment' in rule
            ? rule.setBillingSegment
            : customSegments?.find(segment => Number(segment.segmentId) === rule.setSegmentId)
                ?.segmentName,
        segmentValueName:
          'setBillingSegmentValue' in rule
            ? rule.setBillingSegmentValue
            : customSegments
                ?.flatMap(segment => segment.values)
                .find(value => Number(value.valueId) === rule.setSegmentValueId)?.valueName
      }));
    if (activeTab === 'all') {
      return activeRules;
    }
    return activeRules.filter(rule => rule.segmentName === activeTab);
  }, [activeTab, customSegments, labelingRules, billingRules]);

  const [filter, setFilter] = useStateFunction<
    (line: ModifiedSegmentationMappingLabelRule | ModifiedBillingMappingLabelRule) => boolean
  >(() => true);

  const filteredData = useMemo(
    () => availableRules?.filter(filter) || [],
    [availableRules, filter]
  );

  const handleNavChange = (tab: Tab) => {
    setActiveTab(tab.key);
    if (tab.key === 'all') {
      setFilter(() => true);
    } else {
      setFilter(line => line.segmentName === tab.key);
    }
  };

  const noRowsRenderer = useCallback(() => {
    if (activeTab === 'all') {
      return (
        <div className='no-failures'>
          No rules created for any segment. Select the &quot;Labeling Rules&quot; tab to create a
          rule.
        </div>
      );
    }
    return (
      <div className='no-failures'>
        No rules created for {ruleTabs.find(tab => tab.key === activeTab)?.label || 'this segment'}
      </div>
    );
  }, [activeTab, ruleTabs]);

  const handleSelectRule = useCallback((rule: SegmentationMappingLabelRule) => {
    setSelectedRules(prev => {
      const isAlreadySelected = prev.some(r => r.id === rule.id);
      if (isAlreadySelected) {
        return prev.filter(selectedRule => selectedRule.id !== rule.id);
      }
      return [...prev, rule];
    });
  }, []);

  const ruleHeaders = useMemo<RuleHeader[]>(() => {
    const headers: RuleHeader[] = [
      {
        key: 'name',
        label: 'Rule ID',
        flex: 0.75,
        // Order by the numeric part of the Rule ID, ignoring the BURule/BUBillRule prefix, and keep
        // every BURule ahead of every BUBillRule.
        contentGetter: data => {
          const name = String(data.name ?? '');
          const num = parseInt(name.replace(/\D/g, ''), 10);
          return (name.startsWith('BUBillRule') ? 1e12 : 0) + (Number.isNaN(num) ? 0 : num);
        },
        renderer: data => (
          <div className='select-labeling-rules-selector'>
            <Checkbox
              className='select-labeling-rules-checkbox'
              checked={selectedRules.some(rule => rule.id === data.id)}
              onChange={() => {
                handleSelectRule(data);
              }}
              label=''
            />
            <span className='rule-id'>{data.name}</span>
          </div>
        )
      },
      {
        key: 'ifStatement',
        label: 'If statement',
        flex: isBillingSegmentName(activeTab) ? 2 : 2.25,
        contentGetter: data => data.ifStatement,
        renderer: data => <span className='if-statement'>{data.ifStatement}</span>
      },
      {
        key: 'segmentName',
        label: 'Segment',
        flex: isBillingSegmentName(activeTab) ? 1.25 : 1,
        contentGetter: data =>
          'setBillingSegmentValue' in data ? data.setBillingSegment : data.segmentName,
        renderer: data =>
          'setBillingSegmentValue' in data ? data.setBillingSegment : data.segmentName
      },
      {
        key: 'segmentValueName',
        label: 'Segment Value',
        flex: 1,
        contentGetter: data =>
          'setBillingSegmentValue' in data ? data.setBillingSegmentValue : data.segmentValueName,
        renderer: data =>
          'setBillingSegmentValue' in data ? data.setBillingSegmentValue : data.segmentValueName
      },
      ...(isBillingSegmentName(activeTab)
        ? [
            {
              key: 'dateRange',
              label: 'Date Range',
              flex: 1,
              minFlexWidth: 200,
              contentGetter: data => data.startDate,
              renderer: data => formatDateRange(data.startDate, data.endDate)
            }
          ]
        : [])
    ];

    if (activeTab !== 'all') {
      headers.unshift({
        key: 'rulePriority',
        label: 'Rule Priority',
        nonInteractive: true,
        renderer: data => (
          <span className='first-column'>{activeTab !== 'all' && data.rulePriority}</span>
        ),
        flex: 0.5
      });
    }

    return headers;
  }, [handleSelectRule, selectedRules, activeTab]);

  const tableHeaderRenderer = ({
    data: _data,
    columnIndex
  }: {
    data: unknown;
    columnIndex: number;
  }) => {
    const header = ruleHeaders[columnIndex];
    const isFirstColumn = columnIndex === 0;

    const headerClassName = cn('segment-rules-apply-header', {
      'first-column': isFirstColumn,
      'non-first-header': !isFirstColumn
    });

    if (header.key === 'name') {
      const allSelected = filteredData.every(filteredRule =>
        selectedRules.some(selectedRule => selectedRule.id === filteredRule.id)
      );
      return (
        <Checkbox
          className={headerClassName}
          checked={allSelected}
          onChange={() => {
            if (allSelected) {
              setSelectedRules(prev =>
                prev.filter(
                  selectedRule =>
                    !filteredData.some(filteredRule => filteredRule.id === selectedRule.id)
                )
              );
            } else {
              setSelectedRules(prev => [
                ...prev,
                ...filteredData.filter(
                  filteredRule => !prev.some(prevRule => prevRule.id === filteredRule.id)
                )
              ]);
            }
          }}
          label='Rule ID'
        />
      );
    }

    return <span className={headerClassName}>{header.label}</span>;
  };

  // Filter changes can shrink the result set below the current page; bump the reset key so the
  // table remounts at the top. (Tab changes are handled synchronously via activeTab in the key.)
  useEffect(() => {
    setTableResetKey(key => key + 1);
  }, [filter]);

  const handleClose = useCallback(
    (applyRules: boolean) => {
      if (applyRules) {
        setIsSaving(true);
        // Give the browser a chance to render because the onRuleApply is
        // overloading things.
        setTimeout(async () => {
          try {
            await (onRuleApply(selectedRules) as unknown as Promise<void>);
          } catch {
            setShow(false);
          } finally {
            setIsSaving(false);
          }
        }, 0);
      } else {
        setShow(false);
      }

      setFilter(() => true);
    },
    [onRuleApply, selectedRules, setFilter, setShow, setIsSaving]
  );

  return (
    <Modal
      className='select-labeling-rules-modal'
      show={show}
      onHide={() => setShow(false)}
      size='xl'
    >
      <div ref={modalRef}>
        <Modal.Header className='select-labeling-rules-modal-header' closeButton>
          <span className='main-header'>Apply Labeling Rules</span>
          <span className='selected-rule-count'>
            ({selectedRules.length} rule{selectedRules.length === 1 ? '' : 's'} selected)
          </span>
        </Modal.Header>
        <Modal.Body>
          <div className='select-labeling-rule-filter'>
            <FilterBar
              size={Size.LG}
              options={ruleHeaders.map(header => ({
                name: header.key || '',
                label: header.label || ''
              }))}
              lines={availableRules || []}
              onFilter={setFilter}
              useLocalStorage={false}
              hasAdvanced
            />
          </div>
          <Nav activeKey={activeTab}>
            <div className='select-labeling-rules-nav-tabs'>
              {visibleTabs.map(tab => (
                <Nav.Item key={tab.key}>
                  <Nav.Link
                    className={cn('select-labeling-rule-tab', { active: activeTab === tab.key })}
                    onClick={() => {
                      handleNavChange(tab);
                    }}
                  >
                    {tab.label}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </div>
            {overflowTabs.length > 0 && (
              <div className='nav-overflow'>
                <Nav.Item>
                  <Dropdown
                    overlayPlacement={Placement.RIGHT}
                    size={Size.LG}
                    className='nav-dropdown'
                    menuAlign='end'
                    options={overflowTabs.map(nav => ({
                      label: nav.label,
                      value: nav.key
                    }))}
                    onChange={selectedValue => {
                      const tab: Tab | undefined = overflowTabs.find(
                        nav => nav.key === selectedValue
                      );
                      if (tab) {
                        handleNavChange(tab);
                      }
                    }}
                    placeholder='More...'
                    value={overflowTabs.map(nav => nav.key).includes(activeTab) ? activeTab : ''}
                  />
                </Nav.Item>
              </div>
            )}
          </Nav>
          <div className='select-labeling-rule-table'>
            <BPMTable
              key={`${activeTab}-${tableResetKey}`}
              data={filteredData}
              headers={ruleHeaders}
              alternateColors={false}
              filterBar={false}
              headersRenderer={tableHeaderRenderer}
              noRowsRenderer={noRowsRenderer}
              cellClassName='select-labeling-rules-cell'
              pagination
              limit={limit}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className='cancel-button'
            appearance={Appearance.OUTLINED}
            design={Design.NEUTRAL}
            leadingElement={<MdCancel color={Brand60} />}
            onClick={() => handleClose(false)}
          >
            Cancel
          </Button>
          <Button
            className='apply-button'
            leadingElement={<MdCheck />}
            loading={isSaving}
            onClick={() => handleClose(true)}
            disabled={isSaving}
          >
            {`Apply ${selectedRules.length} Rule${selectedRules.length === 1 ? '' : 's'}`}
          </Button>
        </Modal.Footer>
      </div>
    </Modal>
  );
};
